import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RotateCw } from 'lucide-react';
import { cn } from 'lib/utils';
import AppContext from 'contexts/AppContext';
import { hasRole, ROLES } from 'utils/roleUtils';
import { apiErrorMessage } from 'utils/errorMessage';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/toast';
import AccessDenied from 'pages/AdminAudit/AccessDenied';
import { DESK_NAME, DESKS, journalLink, RealignmentConflictError } from 'api/sectorAlignmentApi';
import type { DeskAnalysis, LastRun, SectorAnalysis, SectorDesk } from 'api/sectorAlignmentApi';
import { isJobRunning, useSectorAnalysis, useSectorRealignmentJob, useStartRealignment } from 'hooks/useSectorAlignment';
import ReferentialPageHeader from '../components/ReferentialPageHeader';
import ReferentialBoundary from '../components/ReferentialBoundary';
import AlignmentTabs from './AlignmentTabs';
import Banner from './Banner';
import DeskCard from './DeskCard';
import RealignDialog from './RealignDialog';
import { dayOf, isStale, timeOf } from './staleness';

interface State {
  desks: DeskAnalysis[];
  lastRun: LastRun;
  analyzing: SectorDesk[];
  justFinished: SectorDesk | null;
  realignDesk: SectorDesk | null;
  realignError: string | null;
  now: number;
}

type Action =
  | { type: 'analyze'; sds: SectorDesk[] }
  | { type: 'analyzed'; sds: SectorDesk[]; desks: DeskAnalysis[]; lastRun?: LastRun }
  | { type: 'finished'; sd: SectorDesk }
  | { type: 'openRealign'; sd: SectorDesk | null }
  | { type: 'realignError'; message: string }
  | { type: 'tick'; now: number };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'analyze':
      return { ...state, analyzing: [...state.analyzing, ...action.sds], justFinished: action.sds.length > 1 ? null : state.justFinished };
    case 'analyzed': {
      const merged = DESKS.flatMap((sd) => action.desks.find((d) => d.sd === sd) ?? state.desks.find((d) => d.sd === sd) ?? []);
      // One entry per request in flight: a rerun overlapping a full analysis keeps its desk busy until both settle.
      const analyzing = action.sds.reduce((left, sd) => left.filter((s, i) => s !== sd || i !== left.indexOf(sd)), state.analyzing);
      return { ...state, desks: merged, lastRun: action.lastRun === undefined ? state.lastRun : action.lastRun, analyzing };
    }
    case 'finished':
      return { ...state, justFinished: action.sd };
    case 'openRealign':
      return { ...state, realignDesk: action.sd, realignError: null };
    case 'realignError':
      return { ...state, realignError: action.message };
    case 'tick':
      return { ...state, now: action.now };
    default:
      return state;
  }
};

/** A failed request (network, 500) reads as « Jira ne répond pas » on each desk it covered (9a/9b). */
const errorDesk = (sd: SectorDesk): DeskAnalysis => ({
  sd, status: 'error', misaligned: 0, closed: 0, stores: [], unreadable: 0, jira: [], ecm: [], impact: [], analyzedAt: new Date().toISOString(),
});

const oldest = (dates: string[]): string => dates.reduce((min, d) => (d < min ? d : min), dates[0] ?? '');

const SectorAlignment = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { currentUser } = useContext(AppContext);
  const { mutateAsync: fetchAnalysis } = useSectorAnalysis();
  const startRealignment = useStartRealignment();
  const [state, dispatch] = useReducer(reducer, {
    desks: [], lastRun: null, analyzing: [], justFinished: null, realignDesk: null, realignError: null, now: Date.now(),
  });
  const hasDesks = useRef(false);
  const lastRunAt = useRef<string | null>(null);
  useEffect(() => {
    hasDesks.current = state.desks.length > 0;
    lastRunAt.current = state.lastRun?.at ?? null;
  }, [state.desks.length, state.lastRun]);

  const analyze = useCallback(
    async (sd?: SectorDesk): Promise<SectorAnalysis | null> => {
      const sds = sd ? [sd] : DESKS;
      dispatch({ type: 'analyze', sds });
      try {
        const result = await fetchAnalysis(sd);
        const desks = Array.isArray(result?.desks) ? result.desks : [];
        dispatch({ type: 'analyzed', sds, desks: sds.map((s) => desks.find((d) => d.sd === s) ?? errorDesk(s)), lastRun: result?.lastRun ?? null });
        return result;
      } catch (error) {
        console.error('Sector alignment analysis failed:', error);
        dispatch({ type: 'analyzed', sds, desks: sds.map(errorDesk) });
        return null;
      }
    },
    [fetchAnalysis]
  );

  // 7e: the job left the running states; re-run its desk (both when nothing was analyzed yet).
  const rerunDesk = useCallback(
    async (sd: SectorDesk) => {
      dispatch({ type: 'finished', sd });
      const previousAt = lastRunAt.current;
      const result = await analyze(hasDesks.current ? sd : undefined);
      const run = result?.lastRun;
      const desk = result?.desks?.find((d) => d.sd === sd);
      if (run?.sd === sd && (!previousAt || run.at > previousAt) && run.outcome === 'done' && desk?.status === 'ok' && desk.misaligned === 0) {
        toast.success(t('{{count}} tickets realigned', { count: run.applied }));
      }
    },
    [analyze, toast, t]
  );

  const isSuperAdmin = hasRole(currentUser?.role, ROLES.SUPERADMIN);
  const { job } = useSectorRealignmentJob(rerunDesk, { enabled: isSuperAdmin });
  const running = isJobRunning(job) ? job : undefined;

  useEffect(() => {
    const timer = setInterval(() => dispatch({ type: 'tick', now: Date.now() }), 60_000);
    return () => clearInterval(timer);
  }, []);

  if (!isSuperAdmin) {
    return <AccessDenied message={t('This page is reserved for super admins.')} />;
  }

  const { desks, lastRun, analyzing, now } = state;
  const busy = analyzing.length > 0;
  const okDesks = desks.filter((d) => d.status === 'ok');
  const lastRunOf = (sd: SectorDesk): LastRun => (lastRun?.sd === sd ? lastRun : null);
  const staleDesks = okDesks.filter((d) => !analyzing.includes(d.sd) && isStale(d.analyzedAt, lastRunOf(d.sd)?.at, now));
  const unreadable = okDesks.reduce((sum, d) => sum + (Number(d.unreadable) || 0), 0);
  const allAligned = !busy && desks.length === DESKS.length && okDesks.length === DESKS.length && okDesks.every((d) => d.misaligned <= 0);
  const cardDesks = desks.length || busy ? DESKS : DESKS.filter((sd) => sd === running?.sd);
  const realignDesk = desks.find((d) => d.sd === state.realignDesk) ?? null;

  const confirmRealign = () => {
    if (!realignDesk) return;
    startRealignment.mutate(realignDesk.sd, {
      onSuccess: () => dispatch({ type: 'openRealign', sd: null }),
      onError: (error) => {
        if (!(error instanceof RealignmentConflictError)) {
          dispatch({ type: 'realignError', message: apiErrorMessage(t, error) });
          return;
        }
        dispatch({ type: 'openRealign', sd: null });
        toast.error(
          error.startedBy
            ? t('A realignment is already running (started by {{name}}). Nothing was started.', { name: error.startedBy })
            : t('A realignment is already running. Nothing was started.')
        );
      },
    });
  };

  const relaunchButton = (small: boolean) => (
    <Button variant="outline" size={small ? 'sm' : 'default'} onClick={() => analyze()} disabled={busy}>
      <RotateCw className={cn(busy && 'animate-spin')} aria-hidden="true" />
      {t('Relaunch the analysis')}
    </Button>
  );

  return (
    <div className="container mx-auto space-y-5 p-6">
      <div className="flex flex-col gap-1">
        <ReferentialPageHeader
          icon={RotateCw}
          title={t('Sector alignment')}
          subtitle={t("Find the tickets left on their store's former sector and realign them.")}
          secondaryAction={desks.length > 0 ? relaunchButton(false) : undefined}
        />
        {lastRun && (
          <p className="pl-[52px] text-[12.5px] text-muted-foreground">
            {t('Last realignment on {{date}} at {{time}} by {{name}} · {{desk}} · {{count}} tickets ·', {
              date: dayOf(lastRun.at),
              time: timeOf(lastRun.at),
              name: lastRun.actor?.name || '—',
              desk: DESK_NAME[lastRun.sd] ?? '',
              count: lastRun.applied,
            })}{' '}
            <Link to={journalLink(lastRun.realignmentId)} className="text-muted-foreground underline underline-offset-[3px]">
              {t('View in the journal')}
            </Link>
          </p>
        )}
      </div>

      {running?.startedBy && running.startedBy !== currentUser?.username && (
        <Banner tone="sky">
          {t('Realignment in progress, started by {{name}} at {{time}}', {
            name: running.startedBy,
            time: timeOf(running.startedAt),
          })}
        </Banner>
      )}
      {staleDesks.length > 0 && (
        <Banner tone="amber" action={relaunchButton(true)}>
          {t('This result dates from {{time}}. The data may have changed.', { time: timeOf(oldest(staleDesks.map((d) => d.analyzedAt))) })}
        </Banner>
      )}

      {desks.length > 0 && (
        <p className="text-[13px] text-muted-foreground">
          {t('Analyzed at {{time}} · Nothing is saved yet.', { time: timeOf(oldest(desks.map((d) => d.analyzedAt))) })}
        </p>
      )}
      {unreadable > 0 && (
        <Banner tone="amber">{t('{{count}} stores could not be read in Jira: incomplete result.', { count: unreadable })}</Banner>
      )}

      {cardDesks.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {cardDesks.map((sd) => (
            <ReferentialBoundary key={sd} message={t('This service desk could not be displayed.')}>
              <DeskCard
                sd={sd}
                desk={desks.find((d) => d.sd === sd)}
                analyzing={analyzing.includes(sd)}
                progress={running?.sd === sd ? running.progress ?? { done: 0, total: 0 } : null}
                busyElsewhere={Boolean(running)}
                lastRun={lastRunOf(sd)}
                justFinished={state.justFinished === sd}
                now={now}
                onRetry={() => analyze(sd)}
                onRealign={() => dispatch({ type: 'openRealign', sd })}
              />
            </ReferentialBoundary>
          ))}
        </div>
      )}

      {busy && desks.length === 0 && (
        <p className="text-[13px] text-muted-foreground">{t('The analysis is not kept: if you leave the page, it will be lost.')}</p>
      )}
      {allAligned && <p className="text-[13.5px] text-muted-foreground">{t('No misaligned ticket on LUXE or DPGP.')}</p>}

      {!busy && desks.length === 0 && (
        <div className="flex flex-col items-center gap-3 px-5 pb-[140px] pt-[120px] text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-100 dark:bg-zinc-800">
            <RotateCw className="h-5 w-5" aria-hidden="true" />
          </span>
          <p className="max-w-[460px] text-sm leading-relaxed text-muted-foreground">
            {t("Compares each ticket's sector with its store's current sector in Jira.")}
            <br />
            {t('Nothing is changed.')}
          </p>
          <Button onClick={() => analyze()}>
            <RotateCw aria-hidden="true" />
            {t('Analyze')}
          </Button>
        </div>
      )}

      {!DESKS.every((sd) => analyzing.includes(sd)) && (
        <ReferentialBoundary message={t('The analysis tables could not be displayed.')}>
          {/* A desk being re-analysed drops out: its old rows would read as current. */}
          <AlignmentTabs desks={desks.filter((d) => !analyzing.includes(d.sd))} />
        </ReferentialBoundary>
      )}

      <ReferentialBoundary message={t('The realignment dialog could not be displayed.')}>
        <RealignDialog
          desk={realignDesk}
          pending={startRealignment.isPending}
          error={state.realignError}
          onCancel={() => dispatch({ type: 'openRealign', sd: null })}
          onConfirm={confirmRealign}
        />
      </ReferentialBoundary>
    </div>
  );
};

export default SectorAlignment;
